import { ExpandLess, ExpandMore, FilterAlt, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import { Button, Chip, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { FC, Fragment, ReactNode, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import RiskSpinner from "../../components/risk-assessment/RiskSpinner";
import { LINK_SOURCE_COMPANY, LINK_TYPES, buildLinkSearch, filterLinks, highlightRange, linkItems } from "../../components/risk-assessment/riskEditorState";
import { useBusyAction } from "../../components/risk-assessment/useBusyAction";
import { useDebouncedValue } from "../../components/risk-assessment/useDebouncedValue";
import { RiskAssessmentService } from "../../services/RiskAssessmentService";
import { RiskEpiCatalogDTO, RiskLinkDTO, RiskLinkTreeDTO } from "../../types/RiskAssessmentDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import './RiskAssessmentLinks.scss';

const TYPE_LABELS: Record<string, string> = { EPI: 'EPIs', EPC: 'EPCs', MEASURE: 'Medidas de controle' };
const SEARCH_DELAY = 250;
const CLOSED_HEIGHT = 56;
const ITEM_HEIGHT = 48;
const OVERSCAN = 6;

const RiskAssessmentLinks: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [tree, setTree] = useState<RiskLinkTreeDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [riskSearch, setRiskSearch] = useState('');
	const [itemSearch, setItemSearch] = useState('');
	const [types, setTypes] = useState<string[]>(LINK_TYPES);
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
	const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
	const [listOffset, setListOffset] = useState(0);
	const { verifyPermission } = useUtils();
	const { showError } = useAlert();
	const { busy, run } = useBusyAction();
	const service = new RiskAssessmentService();

	useEffect(() => {
		const loadLinks = async () => {
			const response = await service.getLinks();
			if (!response) {
				showError('Não foi possível carregar os vínculos. Recarregue a página para tentar novamente.');
			}
			setTree(response?.data || null);
			setLoading(false);
		};
		loadLinks();
		if (!!permissions.length) {
			verifyPermission(permissions, 'RISK_ASSESSMENT');
		}
	}, []);

	const catalog = useMemo(() => new Map((tree?.catalog || [])
		.map(item => [item.code, item] as [number, RiskEpiCatalogDTO])), [tree]);
	const search = useMemo(() => buildLinkSearch(tree?.links || [], catalog), [tree, catalog]);
	const appliedRiskSearch = useDebouncedValue(riskSearch, SEARCH_DELAY);
	const appliedItemSearch = useDebouncedValue(itemSearch, SEARCH_DELAY);
	const visible = useMemo(() => filterLinks(search, appliedRiskSearch, appliedItemSearch, types),
		[search, appliedRiskSearch, appliedItemSearch, types]);
	const searching = !!appliedItemSearch.trim();
	const virtualizer = useWindowVirtualizer({
		count: visible.length,
		estimateSize: (index) => estimateHeight(visible[index]),
		getItemKey: (index) => visible[index].risk,
		overscan: OVERSCAN,
		scrollMargin: listOffset
	});
	const items = virtualizer.getVirtualItems();
	const before = items.length ? items[0].start - listOffset : 0;
	const after = items.length ? virtualizer.getTotalSize() - (items[items.length - 1].end - listOffset) : 0;

	useLayoutEffect(() => {
		if (!listElement) {
			return;
		}
		const measure = () => setListOffset(listElement.getBoundingClientRect().top + window.scrollY);
		measure();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(measure);
		observer.observe(document.body);
		return () => observer.disconnect();
	}, [listElement]);

	useEffect(() => {
		if (window.scrollY > listOffset) {
			window.scrollTo({ top: 0 });
		}
	}, [visible]);

	return (
		<Fragment>
			<AppBar
				modulo="Árvore de vínculos dos riscos"
			/>
			<div className="risk-assessment-links">
				<div className="barra-filtros">
					<div className="titulo">
						<Typography variant="h6">Árvore de vínculos de riscos</Typography>
						<span>{visible.length} de {tree?.links.length || 0} riscos · empresa {LINK_SOURCE_COMPANY}</span>
					</div>
					<div className="campos">
						<TextField size="small" label="Risco" value={riskSearch} onChange={(event) => setRiskSearch(event.target.value)}/>
						<TextField size="small" label="EPI, EPC, medida ou CA" value={itemSearch}
							onChange={(event) => {
								setItemSearch(event.target.value);
								setCollapsed(new Set());
							}}/>
						<div className="tipos">
							{LINK_TYPES.map(type => (
								<Chip
									key={type}
									label={TYPE_LABELS[type]}
									color={types.includes(type) ? 'primary' : 'default'}
									variant={types.includes(type) ? 'filled' : 'outlined'}
									onClick={() => toggleType(type)}
								/>
							))}
						</div>
						<Button size="small" variant="outlined" disabled={!!busy} onClick={() => run('expand', expandAll)}
							startIcon={busy === 'expand' ? <RiskSpinner size={16}/> : <UnfoldMore/>}>
							Expandir todos
						</Button>
						<Button size="small" variant="outlined" disabled={!!busy} onClick={() => run('collapse', collapseAll)}
							startIcon={busy === 'collapse' ? <RiskSpinner size={16}/> : <UnfoldLess/>}>
							Recolher todos
						</Button>
						<Button size="small" variant="outlined" disabled={!!busy} onClick={() => run('clear', clearFilters)}
							startIcon={busy === 'clear' ? <RiskSpinner size={16}/> : <FilterAlt/>}>
							Limpar filtros
						</Button>
					</div>
				</div>
				{loading
					? getLoading()
					: !visible.length
						? <div className="sem-execucoes"><Typography variant="h6">Nenhum risco encontrado.</Typography></div>
						: <div className="riscos" ref={setListElement}>
							<div style={{ height: before }}/>
							{items.map(item => (
								<div key={String(item.key)} className="item-risco" data-index={item.index} ref={virtualizer.measureElement}>
									{renderLink(visible[item.index])}
								</div>
							))}
							<div style={{ height: after }}/>
						</div>
				}
			</div>
		</Fragment>
	);

	function renderLink(link: RiskLinkDTO) {
		const open = isOpen(link);
		return (
			<div className="risco">
				<div className="cabecalho-risco" onClick={() => toggle(link.risk)}>
					{open ? <ExpandLess/> : <ExpandMore/>}
					<span className="nome">{highlight(link.risk, appliedRiskSearch)}</span>
					<span className="contagens">
						{link.epis.length} EPIs · {link.epcs.length} EPCs · {link.measures.length} medidas
					</span>
				</div>
				{open && (
					<div className="blocos">
						{LINK_TYPES.filter(type => types.includes(type)).map(type => (
							<div key={type} className="bloco">
								<span className="titulo-bloco">{TYPE_LABELS[type]} ({linkItems(link, type).length})</span>
								{!linkItems(link, type).length
									? <span className="vazio">Nenhum vínculo</span>
									: <ul>
										{linkItems(link, type).map(item => (
											<li key={item}>
												<span>{highlight(item, appliedItemSearch)}</span>
												{type === 'EPI' && !!(link.caCodesByEpi[item] || []).length && (
													<div className="cas-epi">
														<span className="titulo-cas">CA(s)</span>
														<div className="cas">
															{epiCas(link.caCodesByEpi[item]).map(ca => (
																<span key={ca} className="ca">{highlight(ca, appliedItemSearch)}</span>
															))}
														</div>
													</div>
												)}
											</li>
										))}
									</ul>
								}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}

	function isOpen(link: RiskLinkDTO) {
		return searching ? !collapsed.has(link.risk) : expanded.has(link.risk);
	}

	function estimateHeight(link: RiskLinkDTO) {
		if (!isOpen(link)) {
			return CLOSED_HEIGHT;
		}
		return CLOSED_HEIGHT * 2 + Math.max(...LINK_TYPES.filter(type => types.includes(type))
			.map(type => linkItems(link, type).length), 1) * ITEM_HEIGHT;
	}

	function toggle(risk: string) {
		const update = (previous: Set<string>) => {
			const next = new Set(previous);
			if (next.has(risk)) {
				next.delete(risk);
			} else {
				next.add(risk);
			}
			return next;
		};
		if (searching) {
			setCollapsed(update);
		} else {
			setExpanded(update);
		}
	}

	function expandAll() {
		if (searching) {
			setCollapsed(new Set());
		} else {
			setExpanded(new Set(visible.map(link => link.risk)));
		}
	}

	function collapseAll() {
		if (searching) {
			setCollapsed(new Set(visible.map(link => link.risk)));
		} else {
			setExpanded(new Set());
		}
	}

	function toggleType(type: string) {
		setTypes(previous => previous.includes(type)
			? previous.length === 1 ? previous : previous.filter(item => item !== type)
			: LINK_TYPES.filter(item => item === type || previous.includes(item)));
	}

	function clearFilters() {
		setRiskSearch('');
		setItemSearch('');
		setTypes(LINK_TYPES);
	}

	function epiCas(codes: number[]) {
		return Array.from(new Set(codes.map(code => catalog.get(code)?.ca || '?')));
	}
};

function highlight(text: string, term: string): ReactNode {
	const range = highlightRange(text, term);
	if (!range) {
		return text;
	}
	return (
		<Fragment>
			{text.slice(0, range[0])}
			<mark>{text.slice(range[0], range[1])}</mark>
			{text.slice(range[1])}
		</Fragment>
	);
}

export default RiskAssessmentLinks;
